import * as React                                 from 'react';
import { useTranslation }                         from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import DV                                         from 'ts-data-validator';

import YandexMap from '../../../common/components/map';

import Preloader       from '../../components/containers/preloader';
import Dropdown        from '../../components/fields/dropdown';
import Meta             from '../../components/meta';
import PageHeader      from '../../components/pageHeader';
import { useCommon }   from '../../providers/common';
import { useUser }     from '../../providers/user';
import { useWhereBuy } from '../../providers/whereBuy';

import renderStaticSection from '../_common/utils/renderStaticSection';

import StoreSlider        from './components/StoreSlider';
import TradeStoreItem     from './components/trade-store-item';
import WhereBuyNavigation from './components/where-buy.navigation';

const WhereBuy = function() {
  const history  = useHistory();
  const location = useLocation();
  const match    = useRouteMatch();
  const { t }    = useTranslation();

  const common   = useCommon();
  const user     = useUser();
  const whereBuy = useWhereBuy();

  const mapSectionRef = React.useRef(null);

  const [productType, setProductType]           = React.useState(null);
  const [galleryIsOpen, setGalleryIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery]     = React.useState('');
  const [slides, setSlides]               = React.useState();
  const [selected, setSelected]           = React.useState();

  const allCities          = React.useMemo(() => {
    /**
     * @type {Map<number, {
     *   cityId:           number;
     *   description?:     string;
     *   isIndexed:        boolean;
     *   label:            string;
     *   lat:              number;
     *   lng:              number;
     *   mapZoom:          number;
     *   metaDescription?: string;
     *   metaKeywords?:    string;
     *   metaTitle?:       string;
     *   regionId:         number;
     *   title?:           string;
     *   urlKey:           string;
     * }>}
     */
    const items = new Map();

    whereBuy.cities.forEach((city) => {
      if (whereBuy.items.some((tradeStore) => tradeStore.cityId === city.cityId)) {
        items.set(city.cityId, city);
      }
    });

    return items;
  }, [whereBuy.cities, whereBuy.items]);
  const allRegions         = React.useMemo(() => {
    /**
     * @type {Map<number, {
     *   countryId:        number;
     *   description?:     string;
     *   isIndexed:        boolean;
     *   label:            string;
     *   lat:              number;
     *   lng:              number;
     *   mapZoom:          number;
     *   metaDescription?: string;
     *   metaKeywords?:    string;
     *   metaTitle?:       string;
     *   regionId:         number;
     *   title?:           string;
     *   urlKey:           string;
     * }>}
     */
    const items = new Map();

    whereBuy.regions.forEach((region) => {
      if (whereBuy.items.some((tradeStore) => tradeStore.regionId === region.regionId)) {
        items.set(region.regionId, region);
      }
    });

    return items;
  }, [whereBuy.items, whereBuy.regions]);
  const allTradeStores     = React.useMemo(() => {
    /**
     * @type {Map<number, {
     *   address:        string;
     *   cityId:         number;
     *   contacts:       {
     *     label:                string;
     *     trandeStoreContactId: string;
     *     type:                 'phone' | 'site';
     *   }[];
     *   description?:   string;
     *   image?:         string;
     *   label:          string;
     *   lat:            number;
     *   lng:            number;
     *   mapZoom:        number;
     *   paymentTypes:   string;
     *   payments:       {
     *     label:         string;
     *     paymentTypeId: string;
     *   }[];
     *   regionId:       number;
     *   slider:         [];
     *   tradeStoreId:   number;
     *   typeTradeStore: 'Бренд-зона' | 'Бренд-секция' | 'Дизайн-студия' | 'Монобрендовый магазин' | 'Фирменный салон';
     *   video?: string;
     *   workTime?:      string;
     * }>}
     */
    const items = new Map();

    whereBuy.items.forEach((tradeStore) => {
      items.set(tradeStore.tradeStoreId, {
        ...tradeStore,
        mapZoom: allCities.get(tradeStore.cityId)?.mapZoom ?? allRegions.get(tradeStore.regionId)?.mapZoom ?? 7,
      });
    });

    return items;
  }, [allCities, allRegions, whereBuy.items]);
  const citiesMap          = React.useMemo(() => {
    /**
     * @type {Map<number, Map<number, {
     *   cityId:           number;
     *   description?:     string;
     *   isIndexed:        boolean;
     *   label:            string;
     *   lat:              number;
     *   lng:              number;
     *   mapZoom:          number;
     *   metaDescription?: string;
     *   metaKeywords?:    string;
     *   metaTitle?:       string;
     *   regionId:         number;
     *   title?:           string;
     *   urlKey:           string;
     * }>>}
     */
    const regions = new Map();

    allCities.values()
      .forEach((city) => {
        const items = regions.get(city.regionId) ?? new Map();

        items.set(city.cityId, city);
        regions.set(city.regionId, items);
      });

    return regions;
  }, [whereBuy.cities]);
  const currentCity        = React.useMemo(() => {
    if (!DV.isString(match.params.urlKey)) {
      return;
    }

    return allCities.values()
      .find((city) => {
        return city.urlKey === match.params.urlKey;
      });
  }, [allCities, match.params.urlKey]);
  const currentRegion      = React.useMemo(() => {
    if (!DV.isString(match.params.urlKey)) {
      return;
    }

    return currentCity ? allRegions.get(currentCity.regionId) : allRegions.values()
      .find((region) => region.urlKey === match.params.urlKey);
  }, [currentCity, match.params.urlKey, allRegions]);
  const tradeStoresMap     = React.useMemo(() => {
    /**
     * @type {Map<string, Map<number, Map<number, {
     *   address:        string;
     *   cityId:         number;
     *   contacts:       {
     *     label:                string;
     *     trandeStoreContactId: string;
     *     type:                 'phone' | 'site';
     *   }[];
     *   description?:   string;
     *   image?:         string;
     *   label:          string;
     *   lat:            number;
     *   lng:            number;
     *   paymentTypes:   string;
     *   payments:       {
     *     label:         string;
     *     paymentTypeId: string;
     *   }[];
     *   regionId:       number;
     *   slider:         [];
     *   tradeStoreId:   number;
     *   typeTradeStore: 'Бренд-зона' | 'Бренд-секция' | 'Дизайн-студия' | 'Монобрендовый магазин' | 'Фирменный салон';
     *   video?: string;
     *   workTime?:      string;
     * }>>>}
     */
    const types = new Map();

    allTradeStores.values()
      .forEach((store) => {
        const regionsByType     = types.get(store.typeTradeStore) ?? new Map();
        const citiesByRegion    = regionsByType.get(store.regionId) ?? new Map();
        const tradeStoresByCity = citiesByRegion.get(store.cityId) ?? new Map();

        tradeStoresByCity.set(store.tradeStoreId, store);
        citiesByRegion.set(store.cityId, tradeStoresByCity);
        regionsByType.set(store.regionId, citiesByRegion);
        types.set(store.typeTradeStore, regionsByType);
      });

    return types;
  }, [allTradeStores]);
  const allowedCities      = React.useMemo(() => {
    if (!currentRegion) {
      return Array.from(allCities.values());
    }

    const cities = citiesMap.get(currentRegion.regionId);

    return Array.from(cities.values());
  }, [allCities, currentRegion, citiesMap]);
  const allowedRegions     = React.useMemo(() => {
    return Array.from(allRegions.values());
  }, [allRegions]);
  const allowedTradeStores = React.useMemo(() => {
    const sort = (items) => {
      return items
        .map((item, index) => {
          return {
            index,
            item,
          }
        })
        .sort((a, b) => {
          const diff = a.item.priority - b.item.priority;

          if (diff !== 0) {
            return diff;
          }

          return a.index - b.index;
        })
        .map(({item}) => item);
    };

    return sort(whereBuy.types
      .filter((type) => {
        return (
          (!whereBuy.typeTradeStore || type.valueKey === whereBuy.typeTradeStore) &&
          allTradeStores
            .values()
            .some((tradeStore) => tradeStore.typeTradeStore === type.valueKey)
        );
      }))
      .map((type) => {
        const itemsByType = tradeStoresMap.get(type.valueKey);

        if (currentRegion) {
          const itemsByRegion = itemsByType.get(currentRegion.regionId);

          if (!itemsByRegion) {
            return [];
          }

          if (currentCity) {
            const itemsByCity = itemsByRegion.get(currentCity.cityId);

            if (!itemsByCity) {
              return [];
            }

            return sort(Array.from(itemsByCity.values()))
          }

          return Array.from(itemsByRegion.values())
            .map((itemsByCity) => {
              return sort(Array.from(itemsByCity.values()))
            })
            .flat();
        }

        return Array.from(itemsByType.values())
          .map((itemsByRegion) => {
            return Array.from(itemsByRegion.values())
              .map((itemsByCity) => {
                return sort(Array.from(itemsByCity.values()))
              })
              .flat();
          })
          .flat();
      })
      .flat()
      .filter((item) => {
        if (!productType) {
          return true;
        }

        if (productType === 'all') {
          return item.productTypes.length === 2;
        }

        return item.productTypes.some((type) => {
          return type.productTypeId === productType;
        });
      });
  }, [allTradeStores, currentCity, currentRegion, productType, tradeStoresMap, whereBuy.typeTradeStore, whereBuy.types]);
  const tradeStoreMarkers  = React.useMemo(() => {
    return allowedTradeStores.map((tradeStore) => {
      return {
        address:     tradeStore.address,
        contacts:    tradeStore.contacts,
        id:          tradeStore.tradeStoreId,
        lat:         tradeStore.lat,
        lng:         tradeStore.lng,
        workingTime: tradeStore.workTime,
      };
    });
  }, [allowedTradeStores]);
  const pathname           = React.useMemo(() => {
    return common.getUrlByTemplate('where-buy');
  }, []);
  const selection          = React.useMemo(() => {
    const { regionData } = whereBuy;

    const metaTitle = currentCity?.metaTitle ?? currentCity?.title
      ? `${currentCity.title} - ${t('steelline')}`
      : currentRegion?.metaTitle ?? currentRegion?.title
        ? `${currentRegion.title} - ${t('steelline')}`
        : regionData.metaTitle ?? regionData.title
          ? `${regionData.title} - ${t('steelline')}`
          : t('default_meta_title');

    const get = (key) => {
      return currentCity?.[key] ?? currentRegion?.[key] ?? regionData[key];
    };

    const data = {
      description: { SECTIONS: [] },
      lat:         get('lat') ?? 53.858248,
      lng:         get('lng') ?? 27.701393,
      meta:        {
        // self-canonical: страна/регион/город переключаются чисто на клиенте,
        // без нового запроса — та же политика, что и для остальных
        // клиентски-переключаемых страниц (см. AboutDoors.js)
        canonical:   !isSSR() ? window.location.href : undefined,
        description: get('metaDescription') ?? t('default_meta_description'),
        isIndexed:   get('isIndexed'),
        keywords:    get('metaKeywords') ?? t('default_meta_keywords'),
        title:       metaTitle,
      },
      mapZoom:     get('mapZoom') ?? 6,
      title:       get('title'),
    };

    try {
      data.description = JSON.parse(currentCity?.description ?? currentRegion?.description ?? regionData.description);
    } catch {
      data.description = { SECTIONS: [] };
    }

    return data;
  }, [currentCity, currentRegion, whereBuy.regionData]);

  const getFullAddress       = React.useCallback((store) => {
    const city   = allCities.get(store.cityId);
    const prefix = t('field_cityLabel')
      .charAt(0)
      .toLowerCase();
    const region = allRegions.get(store.regionId);

    return `${region.label}, ${prefix}. ${city.label}, ${store.address}`;
  }, [allCities, allRegions]);
  const handleChangeRegion   = React.useCallback((regionId) => {
    const region = allRegions.get(regionId);

    history.replace({
      pathname: `${pathname}${region ? `/${region.urlKey}` : ''}`,
      state:    {
        noRedirect: true,
      },
    });

    whereBuy.filterTradeStore(null);
  }, [allRegions, pathname]);
  const handleChangeCity     = React.useCallback((cityId) => {
    const city = allCities.get(cityId);

    if (!city) {
      handleChangeRegion(currentRegion.regionId);
    } else {
      history.replace({
        pathname: `${pathname}/${city.urlKey}`,
        state:    {
          noRedirect: true,
        },
      });

      whereBuy.filterTradeStore(null);
    }
  }, [allCities, currentRegion, handleChangeRegion]);
  const handleChangeProductType = React.useCallback((type) => {
    if (productType !== type) {
      setProductType(type);
    }
  }, [productType]);
  const setSelectedMarker = React.useCallback((tradeStore) => {
    const marker = tradeStoreMarkers.find((marker) => {
      return marker.id === tradeStore.tradeStoreId;
    })

    if (marker) {
      setSelected(marker);
    }
  }, [tradeStoreMarkers]);

  React.useEffect(() => {
    if (!whereBuy.initialStateLoaded) {
      whereBuy.getInitialState(match.params.urlKey);
    }
  }, []);
  React.useEffect(() => {
    if (user.location.cityId) {
      const city = allCities.get(user.location.cityId);

      if (city && whereBuy.initialStateLoaded && !location.state?.noRedirect && !match.params.urlKey) {
        handleChangeCity(city.cityId);
      }
    }
  }, [whereBuy.initialStateLoaded]);
  React.useEffect(() => {
    scrollTo({
      top:  0,
      left: 0,
    });
  }, [match.params.urlKey]);
console.log(allowedTradeStores);
  return (
    <>
      <div className={'page-content whereBuy'}>
        <Meta {...selection.meta} />
        <PageHeader>{selection.title}</PageHeader>
        <section className={'top'}>
          <div className={'filter_wrapper'}>
            <p className={'label'}>{t('field_door_types')}</p>
            <Dropdown
              onChangeCallback={handleChangeProductType}
              options={[
                {
                  productTypeId: 'all',
                  label:      t('label.entrance_and_interior_door'),
                },
                ...whereBuy.filter,
              ]}
              labelKey={'label'}
              placeholder={t('label.all')}
              required={false}
              selected={productType}
              valueKey={'productTypeId'}
            />
          </div>
          <WhereBuyNavigation
            cities={allowedCities}
            currentCity={currentCity}
            currentRegion={currentRegion}
            getFullAddress={getFullAddress}
            handleChangeCity={handleChangeCity}
            handleChangeRegion={handleChangeRegion}
            highlight={setSelectedMarker}
            regions={allowedRegions}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            tradeStores={allowedTradeStores}
          />
        </section>
        <section className={'body'}>
          <aside>
            {allowedTradeStores.map((tradeStore) => {
              return (
                <TradeStoreItem
                  cities={allCities}
                  currentCity={currentCity}
                  getFullAddress={getFullAddress}
                  highlight={setSelectedMarker}
                  isHighlighted={tradeStore.tradeStoreId === selected?.id}
                  key={tradeStore.tradeStoreId}
                  map={mapSectionRef}
                  openGallery={(slides) => {
                    setSlides(slides);
                    setGalleryIsOpen(true);
                  }}
                  setSearchQuery={setSearchQuery}
                  tradeStore={tradeStore}
                />
              );
            })}
          </aside>
          <section ref={mapSectionRef}>
            {whereBuy.initialStateLoaded && (
              <YandexMap
                selected={selected}
                center={[selection.lng, selection.lat]}
                markers={tradeStoreMarkers}
                onMarkerClick={(marker) => {
                  setSelected(marker?.id ?? marker);
                  setSearchQuery(marker ? getFullAddress(marker) : '');
                }}
                zoom={selection.mapZoom}
              />
            )}
          </section>
        </section>
        <StoreSlider
          onClose={() => {
            setSlides(void 0);
            setGalleryIsOpen(false);
          }}
          open={galleryIsOpen}
          slides={slides}
        />
        <article className={'bottom_description'}>
          {selection.description?.SECTIONS?.map((section, s) => renderStaticSection(section, s))}
        </article>
      </div>
      {(!whereBuy.initialStateLoaded || whereBuy.loading) && <Preloader />}
    </>
  );
};

export default WhereBuy;